/**
 * `/agents/workflows/$slug/trace` — the trigger trace (RO-3-13).
 *
 * Rewrite of `excellent/app/agents/workflows/[slug]/trace/{page,_trace-table}.tsx`
 * (156 + table LOC). RO-3-02 deferred it here; RO-3-02b ported the four sibling
 * `workflows/*` pages but this fifth one was not in its scope.
 *
 * ## Why this page is worth porting at all — and why peer-Q5 does not gate it
 *
 * PRD §11.5 escalates the completion PRD's Q5 ("is 0 human-created workflows a
 * UI failure, or a signal that workflow AUTHORING isn't the job to be done?")
 * and says that if authoring is not the job, "L3 should not spend a port budget
 * on workflow-authoring UI", naming RO-3-13 as the exposure.
 *
 * This page is not authoring UI. Its own header states the question it exists to
 * answer: *"I armed a trigger and my workflow didn't run — why not?"* — it reads
 * the FIRES ledger, where every firing DECISION is recorded, precisely because
 * the interesting cases (debounced, rate-capped, dead-lettered) produce no run
 * to look at. That is operational diagnosis of workflows the SEEDER created, and
 * it is exactly as valuable under either answer to Q5. Under the "authoring
 * isn't the job" answer it arguably becomes MORE valuable, since every workflow
 * is then seeded and the only question left is why one didn't fire.
 *
 * ## What made this a REWRITE
 *
 * Three server seams called inline while rendering (`getLoopBySlug`,
 * `listLoopTrace`, `readTriggerCardState`), with only the row array handed to a
 * `"use client"` table. The stat strip and the rails band were computed and
 * rendered in the server body, so a mechanical port would have left two thirds
 * of the page behind.
 *
 * ## Decision — the summary is NOT recomputed from `rows`
 *
 * `listLoopTrace` aggregates the WHOLE firing ledger for `summary` while
 * returning only `limit` rows, with a comment in `subscriptions.ts` explaining
 * why: "a stat strip that silently described only the most recent page would
 * understate a busy trigger, which is exactly the kind of quiet dishonesty this
 * feature exists to end." So this route renders `summary` as delivered and never
 * derives a count from `rows`. Recomputing client-side would look tidier and
 * would reintroduce the dishonesty.
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Button,
  Card,
  CardContent,
  DataTable,
  IntentBadge,
  PageHeader,
  StatStrip,
  formatDateTime,
  formatRelativeTime,
  type DataTableColumn,
  type StatStripItem,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  workflowTrace,
  type TriggerTraceRowDto,
} from "@excellent/ui/api/contracts/workflows/trace";
import { rootRoute } from "./__root";

/** Outcome → the badge intent the ledger reads with. */
function outcomeIntent(outcome: string) {
  return outcome === "fired"
    ? "success"
    : outcome === "debounced" || outcome === "rate_capped"
      ? "warning"
      : outcome === "failed"
        ? "danger"
        : "neutral";
}

const columns: DataTableColumn<TriggerTraceRowDto>[] = [
  {
    key: "fired_at",
    header: "When",
    sortable: true,
    render: (r) => (
      <span className="type-subtext text-muted-foreground" title={formatDateTime(r.fired_at)}>
        {formatRelativeTime(r.fired_at)}
      </span>
    ),
  },
  {
    key: "outcome",
    header: "Outcome",
    render: (r) => (
      <IntentBadge intent={outcomeIntent(r.outcome)}>{r.outcome}</IntentBadge>
    ),
  },
  {
    key: "event_type",
    header: "Event",
    render: (r) => <code className="type-subtext">{r.event_type}</code>,
  },
  {
    key: "subscription_name",
    header: "Trigger",
    cellClassName: "text-muted-foreground",
    render: (r) => r.subscription_name,
  },
  {
    key: "run_uid",
    header: "Run",
    render: (r) =>
      r.run_uid ? (
        <Link
          href={`/agents/o/${r.run_uid}`}
          className="type-subtext text-foreground hover:underline"
        >
          open
        </Link>
      ) : (
        // The interesting kind: a decision that produced no run at all.
        <span className="type-subtext text-muted-foreground">—</span>
      ),
  },
  {
    key: "dispatch_detail",
    header: "Detail",
    render: (r) => (
      <span
        className="type-subtext text-muted-foreground line-clamp-1"
        title={r.dispatch_detail ?? undefined}
      >
        {r.dispatch_detail ?? "—"}
      </span>
    ),
  },
];

function TriggerTracePage() {
  const { slug } = agentsWorkflowTraceRoute.useParams();
  const { data, error, isLoading } = useApi(workflowTrace.get, {
    params: { slug },
    query: { limit: 200 },
  });

  if (isLoading && !data) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-muted-foreground">Loading trace…</p>
      </div>
    );
  }

  // The original called `notFound()`; `detail: null` is that condition, rendered
  // with the breadcrumb trail intact.
  if (error || !data?.detail) {
    return (
      <div className="@container/page flex flex-col gap-4 px-6 pt-5 pb-8">
        <PageHeader
          breadcrumbs={[{ label: "Workflows", href: "/agents/workflows" }, { label: "Trigger trace" }]}
          title="Workflow not found"
          description="No workflow matches that slug."
        />
        <Button asChild variant="outline">
          <Link href="/agents/workflows">Back to workflows</Link>
        </Button>
      </div>
    );
  }

  const { title, rows, summary, subscription, autorunEnabled, drainerLive } = data.detail;

  const stats: StatStripItem[] = [
    { title: "Fired", value: String(summary.fired), description: "events that started a run" },
    {
      title: "Dispatched",
      value: String(summary.dispatched),
      description:
        summary.pending > 0 ? `${summary.pending} queued for next tick` : "runner spawned",
    },
    {
      title: "Held back",
      value: String(summary.debounced + summary.rate_capped),
      description: `${summary.debounced} debounced · ${summary.rate_capped} rate-capped`,
    },
    {
      title: "Failed",
      value: String(summary.failed),
      description: "could not dispatch",
      ...(summary.failed > 0 ? { accentColor: "var(--destructive)" } : {}),
    },
  ];

  return (
    <div className="@container/page flex flex-col gap-6 px-6 pt-5 pb-8">
      <PageHeader
        breadcrumbs={[
          { label: "Workflows", href: "/agents/workflows" },
          { label: title, href: `/agents/workflows/${slug}` },
          { label: "Trigger trace" },
        ]}
        title="Trigger trace"
        description="Every firing decision this workflow's triggers made — including the events that did not run."
        toolbar={
          <Button asChild variant="outline">
            <Link href={`/agents/workflows/${slug}`}>Back to workflow</Link>
          </Button>
        }
      />

      {!subscription ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="type-body text-muted-foreground">
              This workflow has no trigger armed — it runs only when someone runs it.
            </p>
            <Button asChild variant="outline">
              <Link href={`/agents/workflows/${slug}#trigger`}>Arm a trigger</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <StatStrip items={stats} variant="standalone" />

          {/* The rails in force. Without these stated, a "held back" count is a
              mystery rather than an explanation. */}
          <Card>
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 py-4 type-subtext text-muted-foreground">
              <span>
                Listening on{" "}
                <code className="text-foreground">{subscription.event_type_prefix}</code>
                {subscription.aggregate_kind ? (
                  <>
                    {" "}
                    where kind is{" "}
                    <code className="text-foreground">{subscription.aggregate_kind}</code>
                  </>
                ) : null}
              </span>
              <span>
                Debounce{" "}
                <span className="text-foreground">{subscription.debounce_seconds}s</span>
              </span>
              <span>
                Cap <span className="text-foreground">{subscription.max_fires_per_hour}/hr</span>
              </span>
              <span className="flex items-center gap-1.5">
                Trigger
                <IntentBadge intent={subscription.enabled ? "success" : "neutral"}>
                  {subscription.enabled ? "enabled" : "off"}
                </IntentBadge>
              </span>
              <span className="flex items-center gap-1.5">
                Dispatch
                <IntentBadge intent={autorunEnabled ? "success" : "warning"}>
                  {autorunEnabled ? "on" : "off"}
                </IntentBadge>
              </span>
              <span className="flex items-center gap-1.5">
                Scheduler
                <IntentBadge intent={drainerLive ? "success" : "warning"}>
                  {drainerLive ? "live" : "down"}
                </IntentBadge>
              </span>
            </CardContent>
          </Card>

          <DataTable
            title="Firing decisions"
            subtitle="Newest first. A row with no run is the interesting kind."
            count={rows.length}
            rows={rows}
            columns={columns}
            rowKey={(r) => `${r.subscription_uid}:${r.event_uid}`}
            sortable
            initialSort={{ key: "fired_at", dir: "desc" }}
            empty="No firing decisions recorded yet."
          />
        </>
      )}
    </div>
  );
}

export const agentsWorkflowTraceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/workflows/$slug/trace",
  component: TriggerTracePage,
});
